'use client';

import { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import {
  Building2,
  CheckCircle2,
  Flame,
  MapPin,
  Navigation,
  Phone,
  Share2,
  Sparkles,
  Users,
} from 'lucide-react';
import type { EnrichedAvailability } from '@/lib/beds24/types';
import { getDisplayName } from '@/types/apartment';
import type { Apartment } from '@/types/apartment';
import { ApartmentGridSkeleton } from '../apartments/ApartmentCardSkeleton';
import AvailabilityApartmentCard from '@/components/availability/AvailabilityApartmentCard';
import SearchFiltersBar from '@/components/shared/SearchFiltersBar';
import DatePickerPopover from '@/components/shared/DatePickerPopover';
import GuestPickerPopover from '@/components/shared/GuestPickerPopover';
import {
  calculateNights,
  calculateDeparture,
  getDateInTimeZoneISO,
} from '@/lib/dateUtils';
import { useGlobalAIChat } from '@/components/ai/GlobalAIChatProvider';
import {
  trackAvailabilityEvent,
  trackCTAEvent,
  trackPostHogEvent,
  type CTAEventProps,
} from '@/lib/analytics/posthog';
import { fireWspReservasConversion } from '@/lib/analytics/gtag-conversion';
import { openTrackedWhatsApp } from '@/lib/whatsapp/open-tracked-whatsapp';
import WhatsAppGlyph from '@/components/whatsapp/WhatsAppGlyph';
import { companyConfig } from '@/lib/config-loader';
import dynamic from 'next/dynamic';
// A7 (plan v3.1.1): sheet client-only sin SEO — fuera del bundle de hidratación.
const WhatsAppIntakeSheet = dynamic(() => import('@/components/whatsapp/WhatsAppIntakeSheet'), { ssr: false });
import {
  buildRecommendedGroupOption,
  formatShortDate,
  RecommendedGroupCard,
} from '@/components/availability/recommended-group';
import { useAvailabilityFilters } from '@/components/availability/useAvailabilityFilters';
import { buildSearchMessage } from '@/lib/whatsapp/qualified-message';
import { formatMoney } from '@/components/apartments/card/apartment-card-utils';
import AssignmentProductSearchCard from '@/components/availability/AssignmentProductSearchCard';
import AssignmentGroupOptionsBlock from '@/components/availability/AssignmentGroupOptionsBlock';
import { SHOW_ASSIGNMENT_RESULTS } from '@/components/availability/assignment-results';
import { mergeSearchResults } from '@/lib/assignment/merge-search-results';
import type { SearchResultItem } from '@/lib/assignment/public-types';
import type { GroupOptionDTO } from '@/lib/assignment/quote';

interface ChildAge {
  id: string;
  age: '4-' | '5+';
}

interface ResolvedSearch {
  arrival: string;
  departure: string;
  nights: number;
  capacityAdults: number;
  guestAdults: number;
  children: ChildAge[];
  totalPeople: number;
}

function parseChildrenAges(raw: string): ChildAge[] {
  return raw
    .split(',')
    .filter((age): age is ChildAge['age'] => age === '4-' || age === '5+')
    .slice(0, 10)
    .map((age, index) => ({ id: `url-child-${index}`, age }));
}

function parsePositiveInt(raw: string, fallback = 2): number {
  if (!/^\d+$/.test(raw)) return fallback;
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : fallback;
}

/**
 * Feature flag: mostrar bloque "Disponibilidad bajo petición" en /reservas.
 *
 * Brief 2026-05-18 P1: el founder no quiere mostrar unidades bajo petición en el
 * flujo principal de Ads/SEO porque confunden y producen leads manuales de baja
 * calidad. La sección y el código legacy se mantienen — solo se oculta la UI con
 * este flag. Para reactivarla: cambiar a `true`.
 */
const SHOW_ON_REQUEST_RESULTS = false;

/**
 * A los ~3 s de espera se muestra un mensaje que explica la demora (la consulta
 * pega al calendario de reservas en tiempo real). Antes el usuario solo veía
 * 8 esqueletos sin explicación durante 10-15 s.
 */
const SLOW_HINT_MS = 3_000;

/**
 * Timeout recuperable de la búsqueda. Una consulta fría legítima puede tardar
 * ~15-18 s (timeout interno de Beds24 = 15 s + procesamiento); pasado este
 * límite se aborta el request y se ofrece reintentar o seguir por WhatsApp
 * con las fechas/personas ya preservadas.
 */
const SEARCH_TIMEOUT_MS = 25_000;

export default function AvailabilitySearch() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const dateButtonRef = useRef<HTMLButtonElement>(null);
  const guestButtonRef = useRef<HTMLButtonElement>(null);

  // Parámetros de URL + contexto de scope (hook extraído)
  const {
    urlArrival,
    urlDeparture,
    urlNumAdults,
    urlGuestAdults,
    urlChildrenAges,
    urlQuery,
    urlBuilding,
    urlNeighborhood,
    urlSource,
    urlIntent,
    contextHeading,
    contextScope,
  } = useAvailabilityFilters(searchParams);

  // Hook para chat global de IA
  const { updateContext, openChat } = useGlobalAIChat();

  const [arrival, setArrival] = useState(urlArrival);
  const [nights, setNights] = useState(
    urlArrival && urlDeparture ? calculateNights(urlArrival, urlDeparture) : 2
  );
  const [numAdults, setNumAdults] = useState(
    parsePositiveInt(urlGuestAdults || urlNumAdults)
  );
  const [children, setChildren] = useState<ChildAge[]>(() =>
    parseChildrenAges(urlChildrenAges)
  );

  // Handlers for date changes
  const handleArrivalChange = (newArrival: string) => {
    setArrival(newArrival);
  };

  const handleNightsChange = (newNights: number) => {
    setNights(newNights);
  };

  // Calcular totales de huéspedes
  const totalAdults = useMemo(() => {
    const children5Plus = children.filter(child => child.age === '5+').length;
    return numAdults + children5Plus;
  }, [numAdults, children]);

  const smallChildren = useMemo(() => {
    return children.filter(child => child.age === '4-').length;
  }, [children]);

  // Calcular departure automáticamente basado en arrival + nights (memoizado)
  const departure = useMemo(
    () => (arrival ? calculateDeparture(arrival, nights) : ''),
    [arrival, nights]
  );

  const [isLoading, setIsLoading] = useState(false);
  const [searchCooldown, setSearchCooldown] = useState(false);
  const [results, setResults] = useState<EnrichedAvailability[] | null>(null);
  const [restrictedResults, setRestrictedResults] = useState<
    EnrichedAvailability[] | null
  >(null);
  const [restrictedByCheckInOutResults, setRestrictedByCheckInOutResults] = useState<
    EnrichedAvailability[] | null
  >(null);
  const [onRequestResults, setOnRequestResults] = useState<any[] | null>(null);
  const [groupSearch, setGroupSearch] = useState<{
    isLargeGroup: boolean;
    requestedGuests: number;
    maxCapacity: number;
    apartmentsNeeded: number;
  } | null>(null);
  const [resolvedSearch, setResolvedSearch] = useState<ResolvedSearch | null>(null);
  // V2 ("asignación como anuncio normal"): grilla unificada + combos. Solo se
  // pueblan cuando /api/availability devuelve `results` (flag ON). Si son null,
  // se usa el carril V1 (cards nombradas + AssignmentResults aparte).
  const [searchResultsV2, setSearchResultsV2] = useState<SearchResultItem[] | null>(null);
  const [groupOptionsV2, setGroupOptionsV2] = useState<GroupOptionDTO[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Mensaje progresivo a los ~3 s de espera (SLOW_HINT_MS)
  const [showSlowHint, setShowSlowHint] = useState(false);
  // true solo para errores de red/timeout: habilita "Reintentar" + WhatsApp
  const [canRetry, setCanRetry] = useState(false);
  const searchRequestIdRef = useRef(0);
  // AbortController del fetch en curso: se cancela al lanzar otra búsqueda o
  // al desmontar la página (sin requests huérfanos).
  const activeControllerRef = useRef<AbortController | null>(null);
  // Última búsqueda ejecutada — para el botón "Reintentar" tras timeout/red.
  const lastSearchRef = useRef<{ arrival: string; departure: string; numAdults: number } | null>(null);

  // Cancelar el request en vuelo si el usuario sale de la página.
  useEffect(() => {
    return () => {
      activeControllerRef.current?.abort();
    };
  }, []);

  // Actualizar contexto global cuando cambien fechas/guests/resultados
  useEffect(() => {
    const contextSearch = resolvedSearch;
    updateContext({
      currentPage: 'results',
      dates: {
        checkIn: contextSearch?.arrival ?? arrival,
        nights: contextSearch?.nights ?? nights,
        checkOut: contextSearch?.departure ?? departure,
      },
      guests: contextSearch?.totalPeople ?? totalAdults + smallChildren,
      query: urlQuery,
      availableApartments: results ? results.map((apt: EnrichedAvailability) => ({
        id: apt.roomId.toString(),
        name: getDisplayName(apt.apartment),
        totalPrice: apt.totalPrice,
        pricePerNight: apt.pricePerNight,
      })) : [],
    });
  }, [
    resolvedSearch,
    arrival,
    nights,
    departure,
    totalAdults,
    smallChildren,
    urlQuery,
    results,
    updateContext,
  ]);

  // Estados para UI - Pop-overs
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showGuestPicker, setShowGuestPicker] = useState(false);

  // Estado para feedback de error visual en fechas
  const [dateError, setDateError] = useState(false);
  const [dateErrorMessage, setDateErrorMessage] = useState('Selecciona una fecha de entrada válida');

  // Ref para el formulario
  const searchBarRef = useRef<HTMLFormElement>(null);

  // Ref para rastrear si ya se abrió el chat automáticamente para este query
  const hasOpenedChatForQuery = useRef(false);

  // Fecha mínima: hoy
  const today = getDateInTimeZoneISO();

  // Función de búsqueda reutilizable
  const performSearch = async (
    searchArrival: string,
    searchDeparture: string,
    searchNumAdults: number,
    updateUrl: boolean = true
  ) => {
    const requestId = ++searchRequestIdRef.current;
    const requestGuestAdults = numAdults;
    const requestChildren = children.map((child) => ({ ...child }));
    // Cancelar el request anterior si sigue en vuelo (cambio de búsqueda):
    // sin esto quedaban fetch huérfanos consumiendo red/servidor.
    activeControllerRef.current?.abort();
    const controller = new AbortController();
    activeControllerRef.current = controller;
    lastSearchRef.current = {
      arrival: searchArrival,
      departure: searchDeparture,
      numAdults: searchNumAdults,
    };
    setError(null);
    setCanRetry(false);
    setShowSlowHint(false);
    setResults(null);
    setRestrictedResults(null);
    setRestrictedByCheckInOutResults(null);
    setOnRequestResults(null);
    setGroupSearch(null);
    setResolvedSearch(null);
    setSearchResultsV2(null);
    setGroupOptionsV2(null);
    setIsLoading(true);

    // Mensaje progresivo: si a los ~3 s no ha respondido, explicar la espera.
    const slowHintTimer = setTimeout(() => {
      if (requestId === searchRequestIdRef.current) {
        setShowSlowHint(true);
      }
    }, SLOW_HINT_MS);

    // Timeout recuperable: aborta y ofrece reintentar (sin dejar el fetch vivo).
    let timedOut = false;
    const timeoutTimer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, SEARCH_TIMEOUT_MS);

    try {
      const params = new URLSearchParams({
        arrival: searchArrival,
        departure: searchDeparture,
        numAdults: searchNumAdults.toString(),
      });
      // Preservar filtros de scope y atribución al refrescar URL y al pegarle al API.
      if (urlBuilding) params.set('building', urlBuilding);
      if (urlNeighborhood) params.set('neighborhood', urlNeighborhood);
      if (urlSource) params.set('source', urlSource);
      params.set('guestAdults', String(requestGuestAdults));
      if (requestChildren.length > 0) {
        params.set(
          'childrenAges',
          requestChildren.map((child) => child.age).join(',')
        );
      }

      // Actualizar URL sin recargar la página
      if (updateUrl) {
        router.replace(`/reservas?${params.toString()}`, { scroll: false });
      }

      // slim=1 SOLO en la llamada al API (no en la URL del navegador): el
      // backend omite las nombradas duplicadas dentro de `results` (V2) y la
      // grilla se rearma acá con mergeSearchResults. Ahorra ~120 KB por respuesta.
      const apiParams = new URLSearchParams(params);
      apiParams.set('slim', '1');

      const response = await fetch(`/api/availability?${apiParams}`, {
        signal: controller.signal,
      });
      const data = await response.json();
      if (requestId !== searchRequestIdRef.current) return;

      if (!response.ok || !data.success) {
        setError(data.error || 'Error consultando disponibilidad');
        return;
      }

      // Usar las versiones "flat" que son arrays (la API también devuelve versiones agrupadas por own/partner)
      const available = data.availableApartmentsFlat || [];
      setResults(available);
      setRestrictedResults(data.restrictedByMinStayFlat || []);
      setRestrictedByCheckInOutResults(data.restrictedByCheckInOutFlat || []);
      setOnRequestResults(data.onRequestFlat || []);
      setGroupSearch(data.groupSearch || null);
      // V2: si el backend (flag ON) devolvió la grilla unificada, la usamos.
      setSearchResultsV2(Array.isArray(data.results) ? data.results : null);
      setGroupOptionsV2(Array.isArray(data.groupOptions) ? data.groupOptions : null);
      setResolvedSearch({
        arrival: searchArrival,
        departure: searchDeparture,
        nights: calculateNights(searchArrival, searchDeparture),
        capacityAdults: searchNumAdults,
        guestAdults: requestGuestAdults,
        children: requestChildren,
        totalPeople: requestGuestAdults + requestChildren.length,
      });

      // Brief 2026-05-18 P1: persistir último search-state en sessionStorage
      // para que la ficha pueda construir "Volver a resultados" aunque la URL
      // se haya recortado (compartir, copia parcial). Solo lo que la ficha
      // necesita reconstruir; no guardamos resultados completos.
      try {
        const availableIds = (available as EnrichedAvailability[])
          .map((apt) => apt.apartment.id)
          .slice(0, 50);
        sessionStorage.setItem(
          'pacartagena:last-search',
          JSON.stringify({
            arrival: searchArrival,
            departure: searchDeparture,
            numAdults: searchNumAdults,
            guestAdults: requestGuestAdults,
            childrenAges: requestChildren.map((child) => child.age),
            feed: availableIds.join(','),
            building: urlBuilding || undefined,
            neighborhood: urlNeighborhood || undefined,
            source: urlSource || undefined,
          })
        );
      } catch {
        // sessionStorage puede no estar disponible (modo privado, cookies bloqueadas).
        // El "Volver a resultados" caerá al fallback de URL params; no es bloqueante.
      }

      // PostHog: medir éxito o fracaso de la consulta.
      const totalLoaded =
        available.length +
        (data.restrictedByMinStayFlat?.length || 0) +
        (data.restrictedByCheckInOutFlat?.length || 0) +
        (data.onRequestFlat?.length || 0);
      const eventName = totalLoaded > 0 ? 'availability_results_loaded' : 'availability_no_results';
      trackAvailabilityEvent(eventName, {
        source: urlSource || undefined,
        scope: contextScope,
        building_id: urlBuilding || undefined,
        neighborhood: urlNeighborhood || undefined,
        arrival: searchArrival,
        departure: searchDeparture,
        num_adults: searchNumAdults,
        result_count: totalLoaded,
      });

      // V2: funnel de asignación — cotización cargada en la grilla unificada.
      const assignmentCount = Array.isArray(data.results)
        ? data.results.filter((r: any) => r?.kind === 'assignment').length
        : 0;
      if (assignmentCount > 0) {
        trackPostHogEvent('assignment_quote_loaded', {
          source: urlSource || 'reservas',
          arrival: searchArrival,
          departure: searchDeparture,
          assignment_count: assignmentCount,
          group_options_count: Array.isArray(data.groupOptions) ? data.groupOptions.length : 0,
        });
      }

      // Nota: Los IDs de apartamentos disponibles ahora se pasan en la URL (parámetro 'feed')
      // Ver ApartmentCard.handleNavigation()
    } catch (err) {
      if (requestId !== searchRequestIdRef.current) return;
      const wasAborted =
        controller.signal.aborted || (err as Error | null)?.name === 'AbortError';
      // Abort por nueva búsqueda o por salir de la página: silencioso.
      // Abort por timeout: error recuperable con reintento.
      if (wasAborted && !timedOut) return;
      console.error('Error:', err);
      setCanRetry(true);
      setError(
        timedOut
          ? 'La consulta de disponibilidad está tardando más de lo normal. Puedes reintentar — tus fechas siguen seleccionadas.'
          : 'Error de conexión. Por favor intenta nuevamente.'
      );
    } finally {
      clearTimeout(slowHintTimer);
      clearTimeout(timeoutTimer);
      if (requestId === searchRequestIdRef.current) {
        setIsLoading(false);
        setShowSlowHint(false);
      }
    }
  };

  // Reintento tras timeout/error de red: re-ejecuta la última búsqueda tal cual.
  const retryLastSearch = () => {
    const last = lastSearchRef.current;
    if (!last) return;
    performSearch(last.arrival, last.departure, last.numAdults, false);
  };

  // Buscar automáticamente si hay parámetros de URL (sin actualizar URL de nuevo)
  useEffect(() => {
    if (urlArrival && urlDeparture) {
      performSearch(
        urlArrival,
        urlDeparture,
        parsePositiveInt(urlNumAdults),
        false
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Abrir chat automáticamente si hay query de IA y la búsqueda terminó (con o sin resultados)
  // Solo se ejecuta UNA VEZ por query para evitar que se reabra en búsquedas subsecuentes
  useEffect(() => {
    if (urlQuery && results !== null && !isLoading && !hasOpenedChatForQuery.current) {
      // Marcar que ya se abrió el chat para este query
      hasOpenedChatForQuery.current = true;

      // Pequeño delay para asegurar que todo esté renderizado
      setTimeout(() => {
        openChat();
      }, 1000);
    }
  }, [urlQuery, results, isLoading, openChat]);

  const submitCurrentSearch = () => {
    // Prevenir búsquedas múltiples rápidas
    if (searchCooldown) return;

    // Reset errores previos
    setDateError(false);
    setDateErrorMessage('Selecciona una fecha de entrada válida');
    setError(null);

    if (!arrival || !departure) {
      setError('Por favor selecciona las fechas de llegada y salida');
      setDateError(true);
      setDateErrorMessage('Selecciona fecha de llegada y salida');
      setShowDatePicker(true);
      return;
    }

    if (nights < 2) {
      setError('Selecciona al menos 2 noches');
      setDateError(true);
      setDateErrorMessage('Selecciona al menos 2 noches');
      setShowDatePicker(true);
      return;
    }

    // Validar que las fechas no sean pasadas
    const arrivalDate = new Date(arrival + 'T00:00:00');
    const todayDate = new Date(today + 'T00:00:00');
    if (arrivalDate < todayDate) {
      setError('La fecha de llegada no puede ser en el pasado');
      setDateError(true);
      setDateErrorMessage('La fecha de llegada no puede ser en el pasado');
      setShowDatePicker(true);
      return;
    }

    // Activar cooldown
    setSearchCooldown(true);
    setTimeout(() => setSearchCooldown(false), 1000);

    performSearch(arrival, departure, totalAdults);
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    submitCurrentSearch();
  };

  // Toggle de pop-overs (cierra el otro)
  const toggleDatePicker = () => {
    const willOpen = !showDatePicker;
    setShowDatePicker(willOpen);
    setShowGuestPicker(false);
    setDateError(false); // Resetear error al abrir
    setDateErrorMessage('Selecciona una fecha de entrada válida');
  };

  const toggleGuestPicker = () => {
    const willOpen = !showGuestPicker;
    setShowGuestPicker(willOpen);
    setShowDatePicker(false);
  };


  // Mensaje WhatsApp pre-contextualizado para mostrar cuando no hay resultados.
  const contextWhatsappMessage = useMemo(() => {
    const messageArrival = resolvedSearch?.arrival ?? arrival;
    const messageDeparture = resolvedSearch?.departure ?? departure;
    const messagePeople = resolvedSearch?.totalPeople ?? totalAdults + smallChildren;
    const fmt = (s: string) =>
      s ? new Date(s + 'T00:00:00').toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
    const fechas = messageArrival && messageDeparture ? ` del ${fmt(messageArrival)} al ${fmt(messageDeparture)}` : '';
    const personas = messagePeople > 0
      ? ` para ${messagePeople} ${messagePeople === 1 ? 'persona' : 'personas'}`
      : '';
    let donde = '';
    if (urlBuilding === 'nuevo-conquistador') donde = ' en el Edificio Nuevo Conquistador';
    else if (urlNeighborhood) donde = ` en ${urlNeighborhood}`;
    return `Hola, busqué disponibilidad${donde}${fechas}${personas} y no encontré opciones publicadas. ¿Tienen alternativas?`;
  }, [resolvedSearch, arrival, departure, totalAdults, smallChildren, urlBuilding, urlNeighborhood]);

  const contextWhatsappHref = useMemo(
    () =>
      `https://wa.me/${companyConfig.contact.whatsapp.number}?text=${encodeURIComponent(contextWhatsappMessage)}`,
    [contextWhatsappMessage]
  );

  // Mensaje WhatsApp para el estado de ERROR/timeout de la búsqueda: preserva
  // fechas y personas para que el equipo confirme disponibilidad a mano.
  const searchErrorWhatsappMessage = useMemo(() => {
    const last = lastSearchRef.current;
    const messageArrival = last?.arrival ?? arrival;
    const messageDeparture = last?.departure ?? departure;
    const messagePeople = totalAdults + smallChildren;
    const fmt = (s: string) =>
      s ? new Date(s + 'T00:00:00').toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
    const fechas = messageArrival && messageDeparture ? ` del ${fmt(messageArrival)} al ${fmt(messageDeparture)}` : '';
    const personas = messagePeople > 0
      ? ` para ${messagePeople} ${messagePeople === 1 ? 'persona' : 'personas'}`
      : '';
    return `Hola, intenté consultar disponibilidad${fechas}${personas} pero la página no me cargó los resultados. ¿Me pueden confirmar disponibilidad por aquí?`;
    // error como dependencia: recalcular cuando aparece un error nuevo (lastSearchRef es ref).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrival, departure, totalAdults, smallChildren, error]);

  // Atribución compartida en cualquier evento cta_click_whatsapp emitido
  // desde /reservas. PostHog autocapture no diferencia "Cotizar grupo" de
  // "Solicitar excepción" sin estos props, así que se construye una sola vez.
  // - source: refleja la landing original (urlSource preservado de Ads/landing)
  //   o cae a 'reservas' si el usuario llegó directo. Cierra el funnel
  //   landing → click → WhatsApp sin depender solo de super-properties de sesión.
  // - result_count: total agregado (disponibles + restringidos + on-request)
  //   cuando la búsqueda terminó. undefined antes del primer resultado.
  const totalResultCount = useMemo(() => {
    if (results === null) return undefined;
    return (
      results.length +
      (restrictedResults?.length || 0) +
      (restrictedByCheckInOutResults?.length || 0) +
      // Solo contar onRequest si la sección se está mostrando (flag P1 brief 2026-05-18)
      (SHOW_ON_REQUEST_RESULTS ? (onRequestResults?.length || 0) : 0)
    );
  }, [results, restrictedResults, restrictedByCheckInOutResults, onRequestResults]);

  const ctaAttribution = useMemo(
    () => ({
      source: urlSource || 'reservas',
      arrival: resolvedSearch?.arrival || undefined,
      departure: resolvedSearch?.departure || undefined,
      num_adults: resolvedSearch?.capacityAdults,
      scope: contextScope,
      building_id: urlBuilding || undefined,
      neighborhood: urlNeighborhood || undefined,
      result_count: totalResultCount,
    }),
    [urlSource, resolvedSearch, contextScope, urlBuilding, urlNeighborhood, totalResultCount]
  );

  const recommendedGroupOption = useMemo(() => {
    if (!groupSearch?.isLargeGroup || !results?.length) return null;
    return buildRecommendedGroupOption(results, groupSearch.requestedGuests);
  }, [groupSearch, results]);

  // Sticky mobile contextual — brief 2026-05-18 P1:
  // Solo aparece cuando hay resultados cargados (success o no-results) o
  // restricciones. Si solo hay fechas pero la búsqueda aún no terminó, no
  // mostramos el sticky para evitar la sensación de "todo es WhatsApp" antes
  // de que el usuario vea opciones.
  const hasAnyResults = results !== null && !isLoading;
  const showStickyWhatsapp = Boolean(resolvedSearch) && hasAnyResults;

  // "Dónde" canónico para los mensajes calificados (mismo criterio que el
  // encabezado): edificio nombrado o barrio. Reusado por sticky/banner/beacon.
  const searchDonde = useMemo(() => {
    const buildingName = urlBuilding === 'nuevo-conquistador' ? 'Edificio Nuevo Conquistador' : undefined;
    const neighborhood = !buildingName && urlNeighborhood ? urlNeighborhood : undefined;
    return { buildingName, neighborhood };
  }, [urlBuilding, urlNeighborhood]);

  // Rango de precios de los apartamentos DISPONIBLES (los del grid principal).
  // `totalPrice` es el mismo total por estancia que muestra cada card, así que
  // el mensaje cita exactamente lo que el cliente vio (visión founder 2026-06-11:
  // el mensaje WhatsApp va con precio, no frío). Solo cuenta los que tienen
  // precio > 0 para que el rango sea real.
  const searchPriceRange = useMemo(() => {
    if (!results || results.length === 0 || !resolvedSearch) return null;
    const totals = results
      .map((r) => r.totalPrice)
      .filter(
        (n): n is number =>
          typeof n === 'number' && Number.isFinite(n) && n > 0
      );
    if (totals.length === 0) return null;
    return {
      count: totals.length,
      minFormatted: formatMoney(Math.min(...totals)),
      maxFormatted: formatMoney(Math.max(...totals)),
      nights: resolvedSearch.nights,
    };
  }, [results, resolvedSearch]);

  const stickyWhatsappMessage = useMemo(() => {
    const search = resolvedSearch;
    if (!search) {
      return 'Hola, quiero consultar disponibilidad de apartamentos en Cartagena.';
    }
    const numChildren = search.children.length;
    // Con opciones disponibles y precio → mensaje calificado con rango.
    if (searchPriceRange) {
      return buildSearchMessage({
        arrival: search.arrival,
        departure: search.departure,
        guests: search.totalPeople,
        numAdults: search.guestAdults,
        numChildren: numChildren || undefined,
        ...searchDonde,
        priceRange: searchPriceRange,
      });
    }
    // Sin precio (búsqueda en curso, sin resultados o solo restringidos) →
    // mensaje contextual previo, sin inventar montos.
    const fmt = (s: string) =>
      s ? new Date(s + 'T00:00:00').toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
    const fechas = ` del ${fmt(search.arrival)} al ${fmt(search.departure)}`;
    const personas = search.totalPeople > 0
      ? ` para ${search.totalPeople} ${search.totalPeople === 1 ? 'persona' : 'personas'}`
      : '';
    let donde = '';
    if (urlBuilding === 'nuevo-conquistador') donde = ' en el Edificio Nuevo Conquistador';
    else if (urlNeighborhood) donde = ` en ${urlNeighborhood}`;
    const totalFlat = (results?.length || 0)
      + (restrictedResults?.length || 0)
      + (restrictedByCheckInOutResults?.length || 0)
      + (SHOW_ON_REQUEST_RESULTS ? (onRequestResults?.length || 0) : 0);
    const cierre = results === null
      ? '¿Pueden ayudarme a confirmar disponibilidad?'
      : totalFlat > 0
        ? 'Estuve viendo opciones disponibles y necesito ayuda para reservar.'
        : 'No encontré opciones publicadas para esas fechas. ¿Tienen alternativas?';
    return `Hola, busqué disponibilidad${donde}${fechas}${personas}. ${cierre}`;
  }, [resolvedSearch, searchPriceRange, searchDonde, urlBuilding, urlNeighborhood, results, restrictedResults, restrictedByCheckInOutResults, onRequestResults]);

  const stickyWhatsappHref = useMemo(() => {
    return `https://wa.me/${companyConfig.contact.whatsapp.number}?text=${encodeURIComponent(stickyWhatsappMessage)}`;
  }, [stickyWhatsappMessage]);

  // Publicar el rango en el dataset del body para que el CTA WhatsApp global
  // (header/footer) lo cite en su bypass sin recalcular. Amarrado a SUS fechas
  // (searchArrival/Departure): el resolutor solo lo usa si coinciden con la URL.
  useEffect(() => {
    const ds = document.body.dataset;
    const clear = () => {
      delete ds.wspSearchArrival;
      delete ds.wspSearchDeparture;
      delete ds.wspSearchCount;
      delete ds.wspSearchMin;
      delete ds.wspSearchMax;
      delete ds.wspSearchNights;
    };
    if (searchPriceRange && resolvedSearch) {
      ds.wspSearchArrival = resolvedSearch.arrival;
      ds.wspSearchDeparture = resolvedSearch.departure;
      ds.wspSearchCount = String(searchPriceRange.count);
      ds.wspSearchMin = searchPriceRange.minFormatted;
      ds.wspSearchMax = searchPriceRange.maxFormatted;
      ds.wspSearchNights = String(searchPriceRange.nights);
    } else {
      clear();
    }
    return clear;
  }, [searchPriceRange, resolvedSearch]);

  // Reservar espacio inferior para la sticky bar mobile a nivel <body>.
  // El padding interno del wrapper no alcanza: el componente está dentro de
  // <main> y luego viene la sección "Cómo funciona" + el Footer global, que
  // sí podrían quedar tapados al hacer scroll. Aplicar al body es lo que
  // garantiza que el último píxel del documento siga siendo legible.
  // Mobile-only via matchMedia + listener, con safe-area para iPhone X+.
  useEffect(() => {
    if (!showStickyWhatsapp) return;
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(max-width: 767.98px)');
    const body = document.body;
    const previousPaddingBottom = body.style.paddingBottom;
    const apply = () => {
      if (mql.matches) {
        body.style.paddingBottom = 'calc(6rem + env(safe-area-inset-bottom))';
      } else {
        body.style.paddingBottom = previousPaddingBottom;
      }
    };
    apply();
    mql.addEventListener('change', apply);
    return () => {
      mql.removeEventListener('change', apply);
      body.style.paddingBottom = previousPaddingBottom;
    };
  }, [showStickyWhatsapp]);

  // Deep-link calificado — Etapa 2: /reservas?intent=wsp abre el asistente si
  // la URL no trae fechas (si las trae, la búsqueda ya corre sola y el asistente
  // sobra). Lo usan los anuncios de Ads "Escríbenos por WhatsApp" y canales
  // externos: el clic aterriza consultando fechas, no en un chat frío.
  const [intakeOpen, setIntakeOpen] = useState(false);
  useEffect(() => {
    if (urlIntent === 'wsp' && !urlArrival && !urlDeparture) {
      setIntakeOpen(true);
    }
    // Solo al montar — el param no cambia sin navegación.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Telemetría handoff W1: cuántas búsquedas con resultados VEN un prompt de
  // WhatsApp (franja desktop o sticky mobile). Dedup por búsqueda, no por sesión.
  const promptSeenKeyRef = useRef<string>('');
  useEffect(() => {
    if (!showStickyWhatsapp || !resolvedSearch) return;
    const key = `${resolvedSearch.arrival}|${resolvedSearch.departure}|${resolvedSearch.capacityAdults}`;
    if (promptSeenKeyRef.current === key) return;
    promptSeenKeyRef.current = key;
    trackPostHogEvent('availability_results_whatsapp_prompt_seen', {
      ...ctaAttribution,
    });
  }, [showStickyWhatsapp, resolvedSearch, ctaAttribution]);

  const resultArrival = resolvedSearch?.arrival ?? arrival;
  const resultDeparture = resolvedSearch?.departure ?? departure;
  const resultNights = resolvedSearch?.nights ?? nights;
  const resultCapacityAdults = resolvedSearch?.capacityAdults ?? totalAdults;

  // V2: lista unificada para la grilla (named + assignment). Si el backend no
  // mandó `results` (flag OFF), envolvemos las nombradas y solo sale el carril V1.
  const childrenAgesStr = useMemo(
    () => (resolvedSearch?.children ?? []).map((c) => c.age),
    [resolvedSearch]
  );
  const resultGuestAdults = resolvedSearch?.guestAdults ?? numAdults;
  const gridItems: SearchResultItem[] = useMemo(() => {
    const named: SearchResultItem[] = (results ?? []).map((a) => ({
      kind: 'named' as const,
      key: `apartment:${a.apartment.id}` as const,
      apartment: a,
    }));
    if (!searchResultsV2) return named;
    // V2 slim: el backend ya NO duplica las nombradas dentro de `results`
    // (van completas en availableApartmentsFlat). Acá se rearma la grilla con
    // la MISMA función pura de orden que usa el servidor. Se ignoran posibles
    // items `named` del payload (robustez ante un backend pre-slim): las
    // nombradas siempre salen de `results` flat para no duplicar cards.
    const assignmentProducts = searchResultsV2
      .filter(
        (r): r is Extract<SearchResultItem, { kind: 'assignment' }> =>
          r.kind === 'assignment'
      )
      .map((r) => r.product);
    return mergeSearchResults(named, assignmentProducts);
  }, [searchResultsV2, results]);

  // ── F1.5: fusión con el estado vacío (PLAN-ACTIVACION-RESERVA-POR-ASIGNACION §3.5) ──
  // AssignmentResults vive aparte (hermano, no hijo — se monta en
  // app/reservas/page.tsx) y NO hay padre común que comparta estado. Antes
  // de mostrar el mensaje muerto "No hay apartamentos disponibles" hacemos
  // nuestra propia consulta liviana al MISMO endpoint (catálogo en DB + un
  // GET de cupo a Beds24, sin escritura) para saber si existe oferta "por
  // tipo" en estas fechas; si la hay, el mensaje muerto cede el paso al
  // kicker de AssignmentResults, que renderiza justo debajo. null = aún no
  // lo sabemos (no mostramos nada todavía, nunca el mensaje viejo por error).
  const [assignmentFallbackAvailable, setAssignmentFallbackAvailable] = useState<boolean | null>(null);

  // Guard anti "doble vacío" (Finding 3, review F1.5): el hermano solo se
  // monta cuando V2 está OFF (ver app/reservas/page.tsx) y solo renderiza
  // algo si su propio kill switch SHOW_ASSIGNMENT_RESULTS sigue prendido.
  // searchResultsV2 no-null ⟺ el backend fusionó V2 (ASSIGNMENT_LISTING_V2_ENABLED
  // ON) ⟺ AssignmentResults NUNCA se monta — mismo payload que ya decide V2
  // en el backend, sin fetch ni evento nuevo. Si cualquiera de los dos apaga
  // al hermano, confiar en la cotización liviana de abajo dejaría la
  // pantalla sin mensaje muerto Y sin kicker.
  const assignmentSiblingMounted = SHOW_ASSIGNMENT_RESULTS && searchResultsV2 === null;

  useEffect(() => {
    if (results === null || gridItems.length > 0) {
      setAssignmentFallbackAvailable(null);
      return;
    }
    if (!assignmentSiblingMounted) {
      setAssignmentFallbackAvailable(false);
      return;
    }
    const adultsForQuote = resultGuestAdults || resultCapacityAdults;
    if (!resultArrival || !resultDeparture || !adultsForQuote) {
      setAssignmentFallbackAvailable(false);
      return;
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      // Timeout ≠ cleanup: fail-open explícito al mensaje clásico ANTES de abortar.
      // Sin esto, el abort compartido dejaba el estado atascado en null y la búsqueda
      // vacía quedaba sin ningún mensaje (regresión detectada en re-review F1.5).
      setAssignmentFallbackAvailable(false);
      controller.abort();
    }, 6000);
    const params = new URLSearchParams({
      arrival: resultArrival,
      departure: resultDeparture,
      adults: String(adultsForQuote),
      children: String(childrenAgesStr.length),
    });
    if (urlBuilding) params.set('building', urlBuilding);
    if (urlNeighborhood) params.set('neighborhood', urlNeighborhood);

    fetch(`/api/assignment/quote?${params.toString()}`, { signal: controller.signal })
      .then((res) => res.json())
      .then((data) => {
        const assignment = data?.success ? data.assignment : null;
        setAssignmentFallbackAvailable(
          Boolean(assignment && (assignment.products.length > 0 || assignment.groupOptions.length > 0))
        );
      })
      .catch(() => {
        // El cleanup ya abortó este fetch por un cambio de dependencias — no
        // pisar el valor fresco que calcula la corrida siguiente del efecto
        // (Finding 2, review F1.5).
        if (!controller.signal.aborted) setAssignmentFallbackAvailable(false);
      })
      .finally(() => clearTimeout(timeoutId));

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    results,
    gridItems.length,
    assignmentSiblingMounted,
    resultArrival,
    resultDeparture,
    resultGuestAdults,
    resultCapacityAdults,
    childrenAgesStr.length,
    urlBuilding,
    urlNeighborhood,
  ]);

  // ── Fix review F1.5 (Finding 1): señal hacia AssignmentResults (hermano)
  // de si la búsqueda nombrada trajo resultados. La usa para elegir entre el
  // kicker de escasez ("ya se reservaron... pero aún podemos alojarte") y el
  // encabezado neutro — mostrarlo con nombradas disponibles arriba sería una
  // afirmación falsa. Mismo patrón que otros hermanos sin padre común en
  // este repo (CustomEvent en window; ver StickyMobileCTA →
  // AvailabilitySearchForm con 'pacartagena:open-availability-datepicker').
  useEffect(() => {
    if (results === null) return;
    window.dispatchEvent(
      new CustomEvent('pacartagena:availability-named-results', {
        detail: { hasNamedResults: results.length > 0 },
      })
    );
  }, [results]);

  const groupQuoteWhatsappMessage = useMemo(() => {
    if (!groupSearch?.isLargeGroup) return '';
    const from = new Date(resultArrival + 'T00:00:00').toLocaleDateString('es-CO', {
      day: 'numeric',
      month: 'short',
    });
    const to = new Date(resultDeparture + 'T00:00:00').toLocaleDateString('es-CO', {
      day: 'numeric',
      month: 'short',
    });
    return `Hola, somos ${groupSearch.requestedGuests} personas del ${from} al ${to}. Necesitamos combinar ${groupSearch.apartmentsNeeded} apartamentos. ¿Pueden cotizar?`;
  }, [groupSearch, resultArrival, resultDeparture]);

  const groupQuoteWhatsappHref = useMemo(
    () =>
      `https://wa.me/${companyConfig.contact.whatsapp.number}?text=${encodeURIComponent(groupQuoteWhatsappMessage)}`,
    [groupQuoteWhatsappMessage]
  );

  const minStayExceptionMessage = useMemo(() => {
    const from = new Date(resultArrival + 'T00:00:00').toLocaleDateString('es-CO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    const to = new Date(resultDeparture + 'T00:00:00').toLocaleDateString('es-CO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    return `Hola, estuve consultando disponibilidad del ${from} al ${to} para ${resultCapacityAdults} ${resultCapacityAdults === 1 ? 'persona' : 'personas'}, pero los apartamentos tienen restricción de estancia mínima. ¿Podrían considerar una excepción?`;
  }, [resultArrival, resultDeparture, resultCapacityAdults]);

  const minStayExceptionHref = useMemo(
    () =>
      `https://wa.me/${companyConfig.contact.whatsapp.number}?text=${encodeURIComponent(minStayExceptionMessage)}`,
    [minStayExceptionMessage]
  );

  const checkInOutExceptionMessage = useMemo(() => {
    const from = new Date(resultArrival + 'T00:00:00').toLocaleDateString('es-CO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    const to = new Date(resultDeparture + 'T00:00:00').toLocaleDateString('es-CO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    return `Hola, estuve consultando disponibilidad del ${from} al ${to} para ${resultCapacityAdults} ${resultCapacityAdults === 1 ? 'persona' : 'personas'}, pero hay restricción de check-in/check-out por temporada alta. ¿Podrían considerar una excepción?`;
  }, [resultArrival, resultDeparture, resultCapacityAdults]);

  const checkInOutExceptionHref = useMemo(
    () =>
      `https://wa.me/${companyConfig.contact.whatsapp.number}?text=${encodeURIComponent(checkInOutExceptionMessage)}`,
    [checkInOutExceptionMessage]
  );

  return (
    <div className="w-full">
      {/* Encabezado de contexto: solo cuando se buscó filtrado por edificio o barrio. */}
      {contextHeading && (
        <div className="mb-6 flex items-center justify-center gap-2 text-center">
          {urlBuilding ? (
            <Building2 className="h-5 w-5 text-gray-400" strokeWidth={2} />
          ) : (
            <MapPin className="h-5 w-5 text-gray-400" strokeWidth={2} />
          )}
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 tracking-tight">
            {contextHeading}
          </h1>
        </div>
      )}

      {/* Formulario de búsqueda - Diseño Unificado con Pop-overs */}
      <form
        onSubmit={handleSearch}
        className="mb-12 w-full max-w-4xl mx-auto relative"
        ref={searchBarRef}
      >
        {/* BARRA DE BÚSQUEDA CENTRALIZADA */}
        <SearchFiltersBar
          arrival={arrival}
          departure={departure}
          nights={nights}
          onToggleDatePicker={toggleDatePicker}
          dateError={dateError}
          dateButtonRef={dateButtonRef}
          totalAdults={totalAdults}
          smallChildren={smallChildren}
          onToggleGuestPicker={toggleGuestPicker}
          guestButtonRef={guestButtonRef}
          // Brief 2026-05-18 P0: feedback inmediato. Mantener spinner durante el
          // cooldown post-fetch (1s) evita la sensación de "el botón no respondió"
          // cuando el usuario tapea de nuevo justo antes de que vuelvan los resultados.
          isLoading={isLoading || searchCooldown}
        />

        {/* POPOVERS CENTRALIZADOS — anclados al botón trigger (auto-flip + portal en desktop) */}
        <DatePickerPopover
          isOpen={showDatePicker}
          onClose={() => setShowDatePicker(false)}
          onConfirm={() => {
            setShowDatePicker(false);
            setShowGuestPicker(true);
          }}
          arrival={arrival}
          nights={nights}
          onArrivalChange={(value) => {
            handleArrivalChange(value);
            setDateError(false);
            setDateErrorMessage('Selecciona una fecha de entrada válida');
          }}
          onNightsChange={(value) => {
            handleNightsChange(value);
            if (value >= 2) {
              setDateError(false);
              setDateErrorMessage('Selecciona una fecha de entrada válida');
            }
          }}
          minNights={2}
          error={dateError}
          errorMessage={dateErrorMessage}
          className="md:left-4"
          getAnchorEl={() => dateButtonRef.current}
        />

        <GuestPickerPopover
          isOpen={showGuestPicker}
          onClose={() => setShowGuestPicker(false)}
          onConfirm={() => {
            setShowGuestPicker(false);
            // Bug fix 2026-05-05 (agente SEO): el "Listo" de huéspedes no
            // disparaba re-búsqueda — el cliente debía clickear lupa después.
            // Reusa la validación de handleSearch via fake-event.
            if (arrival && departure && !searchCooldown) {
              setSearchCooldown(true);
              setTimeout(() => setSearchCooldown(false), 1000);
              performSearch(arrival, departure, totalAdults);
            }
          }}
          numAdults={numAdults}
          onNumAdultsChange={setNumAdults}
          onChildrenChange={setChildren}
          initialChildren={children}
          className="md:left-40"
          getAnchorEl={() => guestButtonRef.current}
        />
      </form>

      {/* Estado de Carga — skeleton de cards con altura estable.
          Reemplazo del spinner de dots (CLS alto: aparición/desaparición
          de un bloque de 200px en el mismo lugar donde luego renderiza el
          grid de resultados). El skeleton tiene la misma estructura que
          ApartmentCard, así que el navegador no salta cuando llega la
          respuesta y el usuario ve "están cargando 8 apartamentos" en vez
          de "el sitio está pensando". */}
      {isLoading && (
        <div className="mb-8 animate-in fade-in duration-300">
          {/* Mensaje progresivo (~3 s): explica la espera en lugar de dejar
              solo esqueletos. aria-live para lectores de pantalla. */}
          {showSlowHint && (
            <p
              className="mb-4 text-center text-sm text-gray-600"
              role="status"
              aria-live="polite"
            >
              Seguimos consultando la disponibilidad en tiempo real con nuestro
              calendario de reservas… unos segundos más.
            </p>
          )}
          <ApartmentGridSkeleton count={8} />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-8 bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
          <p className="text-red-800 font-semibold">{error}</p>
          {/* Recuperación solo para errores de red/timeout (no de validación):
              reintentar la misma búsqueda o seguir por WhatsApp con las
              fechas/personas ya preservadas. */}
          {canRetry && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={retryLastSearch}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-800 border border-gray-300 hover:border-gray-400 text-sm font-semibold rounded-full transition-colors"
              >
                Reintentar búsqueda
              </button>
              <a
                href={`https://wa.me/${companyConfig.contact.whatsapp.number}?text=${encodeURIComponent(searchErrorWhatsappMessage)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => {
                  event.preventDefault();
                  openTrackedWhatsApp(searchErrorWhatsappMessage, {
                    ...ctaAttribution,
                    cta_position: 'reservas_search_error',
                  });
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-wsp-brand hover:bg-wsp-brand-dark text-wsp-ink text-sm font-semibold rounded-full transition-colors shadow-sm"
              >
                <WhatsAppGlyph size={16} />
                Consultar por WhatsApp
              </a>
            </div>
          )}
        </div>
      )}

      {/* Layout principal */}
      {results !== null && (
        <div className="space-y-8 w-full">
          {/* Apartamentos disponibles (named) y/o productos por asignación (categorías).
              Gate por gridItems: las cards de asignación tienen disponibilidad por
              categoría y deben salir AUNQUE no haya unidades identificadas libres. */}
          {gridItems.length > 0 ? (
            <div>
              {results.length > 0 ? (
              <div className="mb-5 flex flex-wrap items-center gap-3">
                <h2 className="text-2xl font-semibold text-slate-900">
                  {results.length} {results.length === 1 ? 'apartamento disponible' : 'apartamentos disponibles'} para tus fechas
                </h2>
                {/* Scarcity real (Gemini v7 P3-D): solo si ≤3 — activa FOMO
                    legítimo. Si hay 5+, "quedan 5" no genera urgencia. */}
                {results.length <= 3 && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700 border border-orange-200">
                    <Flame className="h-3.5 w-3.5" strokeWidth={2.25} />
                    {results.length === 1
                      ? 'Última opción para tus fechas'
                      : `Solo quedan ${results.length} para tus fechas`}
                  </span>
                )}
                {/* Compartir resultados por WhatsApp — para enviar a un amigo */}
                <button
                  onClick={() => {
                    if (typeof window === 'undefined') return;
                    const url = window.location.href;
                    const fmtDate = (s: string) => new Date(s + 'T00:00:00').toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
                    const fechas = `del ${fmtDate(resultArrival)} al ${fmtDate(resultDeparture)}`;
                    const people = resolvedSearch?.totalPeople ?? resultCapacityAdults;
                    const huespedes = `para ${people} ${people === 1 ? 'huésped' : 'huéspedes'}`;
                    const message = `Mira estos ${results.length} ${results.length === 1 ? 'apartamento' : 'apartamentos'} disponibles en Cartagena ${fechas} ${huespedes}\n${url}`.replace(/\s+/g, ' ').trim();
                    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
                  }}
                  aria-label="Compartir resultados por WhatsApp"
                  className="ml-auto inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:text-emerald-800 px-3 py-1.5 rounded-full border border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 transition-colors"
                >
                  <Share2 className="w-4 h-4" />
                  <span>Compartir disponibles</span>
                </button>
              </div>
              ) : (
                <div className="mb-5">
                  <h2 className="text-2xl font-semibold text-slate-900">Opciones para tus fechas</h2>
                  <p className="mt-1 text-gray-600">
                    No hay unidades específicas libres para esas fechas, pero puedes reservar por categoría —
                    la unidad exacta se confirma al procesar tu reserva.
                  </p>
                </div>
              )}

              {/* Banner grupos grandes — explica que combinamos varios
                  apartamentos cercanos. NO prometemos precio total bundle:
                  hoy no hay algoritmo de bundle pricing, el operador cotiza
                  caso por caso por WhatsApp. El copy es honesto: "te
                  cotizamos" en lugar de "$ X total". */}
              {groupSearch?.isLargeGroup && (
                recommendedGroupOption ? (
                  <RecommendedGroupCard
                    option={recommendedGroupOption}
                    requestedGuests={groupSearch.requestedGuests}
                    arrival={resultArrival}
                    departure={resultDeparture}
                    ctaAttribution={ctaAttribution}
                  />
                ) : (
                  <div className="mb-8 rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm">
                        <Users className="w-5 h-5 text-emerald-700" />
                      </div>
                      <div className="text-sm text-slate-700 leading-snug">
                        <p className="font-semibold text-slate-900">
                          Grupo de {groupSearch.requestedGuests} personas
                        </p>
                        <p className="mt-0.5">
                          Podemos combinar{' '}
                          <span className="font-semibold text-emerald-800">
                            {groupSearch.apartmentsNeeded} apartamentos
                          </span>{' '}
                          en el mismo edificio o edificios vecinos. Te cotizamos por WhatsApp.
                        </p>
                      </div>
                    </div>
                    <a
                      href={groupQuoteWhatsappHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(event) => {
                        event.preventDefault();
                        openTrackedWhatsApp(groupQuoteWhatsappMessage, {
                          ...ctaAttribution,
                          cta_position: 'reservas_group_quote',
                        });
                        fireWspReservasConversion({
                          cta_position: 'reservas_group_quote',
                          arrival: ctaAttribution.arrival,
                          departure: ctaAttribution.departure,
                          num_adults: ctaAttribution.num_adults,
                        });
                      }}
                      className="w-full sm:w-auto px-5 py-2.5 bg-wsp-brand hover:-translate-y-px hover:bg-wsp-brand-dark text-wsp-ink text-sm font-semibold rounded-full transition-all flex items-center justify-center gap-2 shadow-sm whitespace-nowrap"
                    >
                      <WhatsAppGlyph size={16} />
                      <span>Cotizar grupo</span>
                    </a>
                  </div>
                )
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {(() => {
                  // IDs de nombradas disponibles para navegación de videos
                  const availableIds = results.map(apt => apt.apartment.id);
                  return gridItems.map((it, index) => {
                    if (it.kind === 'assignment') {
                      // Card de "producto por asignación" mezclada en la misma grilla.
                      return (
                        <AssignmentProductSearchCard
                          key={it.key}
                          product={it.product}
                          arrival={resultArrival}
                          departure={resultDeparture}
                          nights={resultNights}
                          guests={resolvedSearch?.totalPeople ?? resultCapacityAdults}
                          guestAdults={resultGuestAdults}
                          childrenAges={childrenAgesStr}
                          source={urlSource || undefined}
                          position={index}
                          ctaAttribution={ctaAttribution}
                          priority={index === 0}
                        />
                      );
                    }
                    const apartment = it.apartment;
                    // CRÍTICO: Si es grupo grande, no enviar más huéspedes de los que caben
                    const displayGuests = groupSearch?.isLargeGroup
                      ? Math.min(resultCapacityAdults, apartment.apartment.capacity.guests)
                      : resultCapacityAdults;
                    return (
                      <AvailabilityApartmentCard
                        key={apartment.roomId}
                        item={apartment}
                        arrival={resultArrival}
                        departure={resultDeparture}
                        guests={displayGuests}
                        nights={resultNights}
                        availableApartmentIds={availableIds}
                        ctaAttribution={ctaAttribution}
                        ctaPosition="reservas_available_card"
                        priority={index === 0}
                      />
                    );
                  });
                })()}
              </div>

              {/* Franja contextual desktop — handoff W1: convertir búsqueda en WSP calificado.
                  No modal, no tapa cards. Mobile ya tiene el sticky. */}
              {showStickyWhatsapp && (
                <div className="hidden md:flex items-center justify-between gap-4 rounded-2xl border border-emerald-200 bg-emerald-50/60 px-6 py-4 mt-8">
                  <p className="text-sm text-gray-800">
                    ¿Quieres que revisemos estas fechas contigo? Te respondemos por WhatsApp
                    con disponibilidad confirmada.
                  </p>
                  <a
                    href={stickyWhatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => {
                      event.preventDefault();
                      openTrackedWhatsApp(stickyWhatsappMessage, {
                        ...ctaAttribution,
                        cta_position: 'reservas_results_banner',
                        cta_label: 'Enviar búsqueda por WhatsApp',
                      });
                      fireWspReservasConversion({
                        cta_position: 'reservas_results_banner',
                        arrival: ctaAttribution.arrival,
                        departure: ctaAttribution.departure,
                        num_adults: ctaAttribution.num_adults,
                      });
                    }}
                    className="shrink-0 inline-flex items-center gap-2 rounded-full bg-wsp-brand px-5 py-2.5 text-sm font-semibold text-wsp-ink shadow-sm hover:bg-wsp-brand-dark hover:-translate-y-px transition-transform"
                  >
                    <WhatsAppGlyph size={16} />
                    Enviar búsqueda por WhatsApp
                  </a>
                </div>
              )}
            </div>
          ) : assignmentFallbackAvailable === false ? (
            <div className="text-center py-12 bg-gray-50 rounded-2xl px-6">
              <p className="text-xl font-semibold text-gray-800">
                {contextHeading
                  ? `Sin disponibilidad${urlBuilding === 'nuevo-conquistador' ? ' en el Edificio Nuevo Conquistador' : urlNeighborhood ? ` en ${urlNeighborhood}` : ''} para esas fechas`
                  : 'No hay apartamentos disponibles para las fechas seleccionadas'}
              </p>
              <p className="text-gray-600 mt-2 max-w-xl mx-auto">
                Cambia las fechas en el buscador o consúltanos por WhatsApp con tu plan completo
                — solemos tener alternativas que no están publicadas en tiempo real.
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                {(urlBuilding || urlNeighborhood) && (
                  <a
                    href={`/reservas?arrival=${resultArrival}&departure=${resultDeparture}&numAdults=${resultCapacityAdults}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-800 border border-gray-300 hover:border-gray-400 text-sm font-semibold rounded-full transition-colors"
                  >
                    Ver todos los apartamentos
                  </a>
                )}
                <a
                  href={contextWhatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => {
                    event.preventDefault();
                    openTrackedWhatsApp(contextWhatsappMessage, {
                      ...ctaAttribution,
                      cta_position: 'reservas_no_results',
                    });
                    fireWspReservasConversion({
                      cta_position: 'reservas_no_results',
                      arrival: ctaAttribution.arrival,
                      departure: ctaAttribution.departure,
                      num_adults: ctaAttribution.num_adults,
                    });
                  }}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-wsp-brand hover:bg-wsp-brand-dark text-wsp-ink text-sm font-semibold rounded-full transition-colors shadow-sm"
                >
                  <WhatsAppGlyph size={16} />
                  Pedir alternativa por WhatsApp
                </a>
              </div>
            </div>
          ) : null}
          {/* assignmentFallbackAvailable === true (F1.5 fusión): el mensaje
              muerto de arriba cede el paso — AssignmentResults (hermano,
              montado debajo en app/reservas/page.tsx) trae el kicker de
              reencuadre + las cards. assignmentFallbackAvailable === null
              (aún consultando): tampoco mostramos el mensaje viejo para no
              parpadear contenido incorrecto. */}

          {/* Combos para grupos grandes (V2) — debajo de la grilla, sin 2ª llamada */}
          {groupOptionsV2 && groupOptionsV2.length > 0 && resolvedSearch && (
            <AssignmentGroupOptionsBlock
              groupOptions={groupOptionsV2}
              arrival={resolvedSearch.arrival}
              departure={resolvedSearch.departure}
              adults={resolvedSearch.guestAdults}
              children={resolvedSearch.children.length}
              ctaAttribution={ctaAttribution}
            />
          )}

          {/* Apartamentos con restricción de noches mínimas */}
          {restrictedResults && restrictedResults.length > 0 && (
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Disponibles con estancia mínima
                  </h3>
                  <p className="text-gray-600 mt-1">
                    Estos apartamentos requieren más noches de las seleccionadas
                  </p>
                </div>
                <a
                  href={minStayExceptionHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => {
                    event.preventDefault();
                    openTrackedWhatsApp(minStayExceptionMessage, {
                      ...ctaAttribution,
                      cta_position: 'reservas_min_stay_exception',
                    });
                    fireWspReservasConversion({
                      cta_position: 'reservas_min_stay_exception',
                      arrival: ctaAttribution.arrival,
                      departure: ctaAttribution.departure,
                      num_adults: ctaAttribution.num_adults,
                    });
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-wsp-brand hover:bg-wsp-brand-dark text-wsp-ink text-sm font-medium rounded-full transition-colors shadow-sm whitespace-nowrap"
                >
                  <WhatsAppGlyph size={16} />
                  <span>Solicitar excepción</span>
                </a>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {restrictedResults.map((apartment) => (
                  <AvailabilityApartmentCard
                    key={apartment.roomId}
                    item={apartment}
                    arrival={resultArrival}
                    departure={resultDeparture}
                    guests={resultCapacityAdults}
                    nights={resultNights}
                    ctaAttribution={ctaAttribution}
                    ctaPosition="reservas_min_stay_card"
                    ctaLabel="Pedir excepción"
                    restrictionLabel="Mínimo"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Apartamentos con restricción de check-in/check-out (temporada alta) */}
          {restrictedByCheckInOutResults && restrictedByCheckInOutResults.length > 0 && (
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Disponibles con restricción de fechas
                  </h3>
                  <p className="text-gray-600 mt-1">
                    No se permite check-in/check-out en fechas de temporada alta (31 dic y 1 ene)
                  </p>
                </div>
                <a
                  href={checkInOutExceptionHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => {
                    event.preventDefault();
                    openTrackedWhatsApp(checkInOutExceptionMessage, {
                      ...ctaAttribution,
                      cta_position: 'reservas_checkinout_exception',
                    });
                    fireWspReservasConversion({
                      cta_position: 'reservas_checkinout_exception',
                      arrival: ctaAttribution.arrival,
                      departure: ctaAttribution.departure,
                      num_adults: ctaAttribution.num_adults,
                    });
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-wsp-brand hover:bg-wsp-brand-dark text-wsp-ink text-sm font-medium rounded-full transition-colors shadow-sm whitespace-nowrap"
                >
                  <WhatsAppGlyph size={16} />
                  <span>Solicitar excepción</span>
                </a>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {restrictedByCheckInOutResults.map((apartment) => (
                  <AvailabilityApartmentCard
                    key={apartment.roomId}
                    item={apartment}
                    arrival={resultArrival}
                    departure={resultDeparture}
                    guests={resultCapacityAdults}
                    nights={resultNights}
                    ctaAttribution={ctaAttribution}
                    ctaPosition="reservas_checkinout_card"
                    ctaLabel="Pedir excepción"
                    restrictionLabel="Fechas"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Apartamentos bajo petición (sin integración Beds24).
              Oculto por SHOW_ON_REQUEST_RESULTS=false (brief 2026-05-18 P1).
              Código preservado para reactivación rápida si se decide volver a
              mostrar leads bajo petición. */}
          {SHOW_ON_REQUEST_RESULTS && onRequestResults && onRequestResults.length > 0 && (
            <div className="pt-8 border-t border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Disponibilidad bajo petición
              </h3>
              <p className="text-gray-600 mb-6 text-sm">
                Estos apartamentos están bajo petición y requieren confirmación manual. Contáctanos para consultar disponibilidad y precios.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {onRequestResults.map((apartment: any) => (
                  <AvailabilityApartmentCard
                    key={apartment.id}
                    item={{ apartment }}
                    arrival={resultArrival}
                    departure={resultDeparture}
                    guests={resultCapacityAdults}
                    nights={resultNights}
                    isOnRequest={true}
                    ctaAttribution={ctaAttribution}
                    ctaPosition="reservas_on_request_card"
                    ctaLabel="Consultar este"
                    restrictionLabel="Bajo petición"
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sticky mobile contextual — brief 2026-05-18 P1.
          Solo aparece tras cargar resultados (success/no-results/restricciones).
          Barra compacta de asistencia: Llamar + WhatsApp. NO compite con las
          cards: outline sobrio, no verde llamativo, no full-width único.
          fireWspReservasConversion solo se dispara desde el CTA WhatsApp del
          sticky (whitelist consistente con la regla existente). */}
      {showStickyWhatsapp && (
        <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-[0_-4px_16px_rgba(0,0,0,0.06)] px-3 py-2.5 pb-[calc(0.625rem+env(safe-area-inset-bottom))]">
          <div className="grid grid-cols-2 gap-2">
            <a
              href={`tel:${companyConfig.contact.phone?.replace(/\s+/g, '') || ''}`}
              onClick={() =>
                trackCTAEvent('cta_click_call', {
                  ...ctaAttribution,
                  cta_position: 'reservas_sticky_mobile_call',
                })
              }
              className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl border border-gray-300 bg-white text-gray-900 hover:bg-gray-50 active:bg-gray-100 text-sm font-semibold px-3 py-2.5 transition-colors"
              aria-label="Llamar al equipo de PaCartagena"
            >
              <Phone className="h-4 w-4" strokeWidth={2.25} />
              <span>Llamar</span>
            </a>
            <a
              href={stickyWhatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => {
                event.preventDefault();
                openTrackedWhatsApp(stickyWhatsappMessage, {
                  ...ctaAttribution,
                  cta_position: 'reservas_sticky_mobile',
                });
                fireWspReservasConversion({
                  cta_position: 'reservas_sticky_mobile',
                  arrival: ctaAttribution.arrival,
                  departure: ctaAttribution.departure,
                  num_adults: ctaAttribution.num_adults,
                });
              }}
              // Gemini design+growth 2026-05-18: en LATAM, WhatsApp es la red
              // de contención principal cuando hay fricción. Solid wsp-brand
              // hace que el ojo se vaya al "salvavidas" sin pensar; Llamar
              // queda outline sobrio como acción complementaria.
              className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl bg-wsp-brand text-wsp-ink hover:bg-wsp-brand-dark active:bg-wsp-brand-dark text-sm font-semibold px-3 py-2.5 transition-colors shadow-sm"
              aria-label="Enviar estas fechas por WhatsApp"
            >
              <WhatsAppGlyph size={16} />
              <span>Enviar estas fechas</span>
            </a>
          </div>
        </div>
      )}

      <WhatsAppIntakeSheet
        open={intakeOpen}
        onClose={() => setIntakeOpen(false)}
        context={{
          ctaPosition: 'intake_reservas_deeplink',
          entryContext: 'reservas',
          buildingSlug: urlBuilding || undefined,
          neighborhood: urlNeighborhood || undefined,
          source: urlSource || 'deeplink_wsp',
          mode: 'results',
        }}
      />
    </div>
  );
}
